import cx from 'classnames';
import { useEffect, useMemo, useState } from 'react';
import useAddOnsInfo from '../../hooks/useAddOnsInfo';
import { useDispatch, useSelector } from 'react-redux';
import useSubscriptionPurchase from '../../hooks/useSubscriptionPurchase';
import {
  apiGetAllProductUsedCount,
  apiToCancelOrRestoreActiveClientSubscriptionV2,
  GetAllAddonsProductList,
  GetAllCartProductList,
  GetProRataPrice,
  GetProRataPriceV2,
  removeAllItemsFromCart,
} from '../../api/subscription/subscription';
import {
  ADD_ONS_PRODUCT_BASE_NAME,
  convertStripeCentsToAmount,
  FABRIC_SEAMLESS_PRODUCTION_CREATION_BASE_TYPE,
  getBillingCycleName,
  getSubscriptionListForCombinePlanPurchase,
  MAKE_CHECKOUT_API_CALL,
  navigateToPlans,
  RECURRING_INTERVAL,
} from '../subscriptionComponents/subscriptionHelper';
import PlanBreadcrumb from '../subscriptionComponents/PlanBreadcrumb';
import { BillingFormV2 } from '../subscriptionComponents/BillingForm';
import PaymentMethods from '../subscriptionComponents/PaymentMethods';
import { Billing, BillingCycle } from './Billing';
import styles from './../Pricing.module.scss';
import Button from '../../components/Button';
import PlusIcon from '../../components/icons/PlusIcon';
import MinusIcon from '../../components/icons/MinusIcon';
import { history } from '../../history';
import {
  selectBillingInfo,
  selectCartAddonsList,
  selectDefaultPaymentMethod,
  selectIsMakeCheckoutAPICall,
  selectSegmentDataForBilling,
  subscriptionActions,
} from '../../redux/slicers/subscriptionSlicer';
import { CircularProgress } from '../../common';
import { setOverflowAutoInBody, wait, windowOpen } from '../../helpers/jsHelper';
import { callTrackEvent } from '../../helpers/segmentHelper';
import { MATTRESS_BUILDER_BASE_TYPE, TEXTURE_BASE_TYPE, THREE_3D_MODAL_BASE_TYPE } from '../../constants/constants';
import { userPilotEvents } from '../../common/Userpilot/userpilotHelper';
import { TERMS_OF_USE_ROUTE } from '../../constants/routePathConstants';

const acceptServiceAgreement = () => {
  return (
    <div className={styles.serviceAgreement}>
      <p>
        By confirming payment you agree to our
        <span onClick={() => windowOpen(TERMS_OF_USE_ROUTE, '_blank')} className="ms-1">
          Service-level-agreement
        </span>
        .
      </p>
    </div>
  );
};

export const CheckoutUI = ({
  checkoutInfo,
  goBack,
  handlePurchase,
  billingCycle,
  setBillingCycle,
  loading = false,
  isUploadPlanCheckout = false,
  selectedPrice,
  subscriptionDetails = null,
  isCurrentPlanIsCancelled,
}) => {
  const { userPlans } = useSubscriptionPurchase();
  const checkoutAPICallStatus = useSelector(selectIsMakeCheckoutAPICall);
  const { trackClickEvent } = userPilotEvents();

  const subscriptionId =
    subscriptionDetails?.id ||
    checkoutInfo?.subscriptionId ||
    userPlans?.find(({ plan_details }) =>
      plan_details?.find(({ plan_id }) => +plan_id === +checkoutInfo?.id)
    )?.id;
  const { name, pricing_details } = checkoutInfo || {};

  let _pricing_details =
    pricing_details?.sort((a, b) => a.recurring_interval - b.recurring_interval) || [];

  const [billing, setBilling] = useState(() =>
    selectedPrice
      ? selectedPrice
      : _pricing_details?.find((a) => a.recurring_interval === billingCycle)
  );
  const [proRata, setProRata] = useState(null);
  const [loadingProrate, setLoadingProrate] = useState(false);

  const allSegmentData = useSelector(selectSegmentDataForBilling);
  const dispatch = useDispatch();
  let isDowngradePlanIdAvailable = false;

  useEffect(() => {
    const dataForSegment = JSON.parse(JSON.stringify(allSegmentData));
    if (dataForSegment) {
      dataForSegment['amount'] = proRata || getTotalPrice();
      dispatch(subscriptionActions.setSegmentDataForBilling(dataForSegment));
    }
  }, [proRata]);
  const isCancelAtPeriod = isCurrentPlanIsCancelled;

  const getPlanProRataAmount = async () => {
    setLoadingProrate(true);
    const payload = handleCreatePayloadForProrateAPI();
    if (payload && !isDowngradePlanIdAvailable) {
      const res = await GetProRataPriceV2(payload);
      setLoadingProrate(false);
      if (res?.data?.pro_rata_amount) {
        setProRata(res?.data?.pro_rata_amount?.toFixed(2) || null);
      } else {
        proRata && setProRata(null);
      }
    } else {
      proRata && setProRata(null);
      setLoadingProrate(false);
    }
  };

  useEffect(() => {
    if (subscriptionId) {
      getPlanProRataAmount();
    } else {
      proRata && setProRata(null);
    }
  }, [checkoutInfo, billing]);

  useEffect(() => {
    return () => {
      setOverflowAutoInBody();
    };
  }, []);

  const resetStates = () => {
    if (isUploadPlanCheckout) {
      setBilling(null);
      goBack?.();
    }
  };

  useEffect(() => {
    return () => resetStates();
  }, []);

  useEffect(() => {
    if (_pricing_details?.length === 0 || !billingCycle) return;
    setBilling(_pricing_details?.find((a) => a.recurring_interval === billingCycle));
  }, [_pricing_details, billingCycle]);

  const onChangeBilling = (interval = null, id) => {
    if (!interval) return;
    const newBilling = _pricing_details?.find(
      (a) => a.recurring_interval === interval && +id === +a?.id
    );
    setBilling({ ...newBilling });
    setBillingCycle(interval);
  };

  const getTotalPrice = () => {
    let totalPrice = 0;
    Object.keys(checkoutInfo)?.map((list) => {
      totalPrice =
        +totalPrice +
        (checkoutInfo?.[list]?.qty || 1) *
          +(
            checkoutInfo?.[list]?.unit_amount ||
            checkoutInfo?.[list]?.pricing_details?.unit_amount ||
            0
          );
    });
    return convertStripeCentsToAmount(totalPrice);
  };

  const handleCreatePayload = () => {
    let payload = {
      recurring_interval: billingCycle,
      recurring_interval_count: 1,
      upload_limit_prices: [],
      recurring_prices: [],
      one_time_items: [],
    };
    Object.keys(checkoutInfo)?.map((list) => {
      if (!checkoutInfo[list]) return;
      if (list === TEXTURE_BASE_TYPE || list === THREE_3D_MODAL_BASE_TYPE) {
        payload.upload_limit_prices.push({
          price_id: checkoutInfo[list]?.stripe_price_id,
          quantity: 1,
          product_type: checkoutInfo[list]?.productType,
        });
      }
      if (list === ADD_ONS_PRODUCT_BASE_NAME.Editor) {
        payload.recurring_prices.push({
          price_id: checkoutInfo[list]?.stripe_price_id,
          quantity: checkoutInfo[list]?.qty || 1,
        });
      }
    });
    return payload;
  };

  const handleCreatePayloadForProrateAPI = () => {
    let payload = {
      client_subscription_id: subscriptionId,
      upload_limit_prices: [],
      recurring_prices: [],
      one_time_items: [],
    };
    isDowngradePlanIdAvailable = false;
    Object.keys(checkoutInfo)?.map((list) => {
      if (!checkoutInfo[list]) return;
      if (list === TEXTURE_BASE_TYPE || list === THREE_3D_MODAL_BASE_TYPE) {
        if (
          subscriptionDetails?.plan_details?.find(
            ({ basemodeltype, next_downgrade_price_id }) =>
              next_downgrade_price_id && basemodeltype === list
          )
        ) {
          isDowngradePlanIdAvailable = true;
        }
        payload.upload_limit_prices.push({
          price_id: checkoutInfo[list]?.stripe_price_id,
          quantity: 1,
          product_type: checkoutInfo[list]?.productType,
        });
      }
      if (list === ADD_ONS_PRODUCT_BASE_NAME.Editor) {
        if (
          subscriptionDetails?.plan_details?.find(
            ({ basemodeltype, to_remove_product_quantity }) =>
              +to_remove_product_quantity > 0 && basemodeltype === list
          )
        ) {
          isDowngradePlanIdAvailable = true;
        }
        payload.recurring_prices.push({
          price_id: checkoutInfo[list]?.stripe_price_id,
          quantity: checkoutInfo[list]?.qty || 1,
        });
      }
    });
    return payload;
  };

  const cancelCurrentRequestPlan = async () => {
    if (isCancelAtPeriod) {
      const res = await apiToCancelOrRestoreActiveClientSubscriptionV2({
        id: subscriptionDetails?.id,
        cancel_at_period_end: false,
        successMessage: '',
        errorMessage: '',
      });
      return res;
    }
    return null;
  };

  const handlePayment = async () => {
    dispatch(subscriptionActions.setUpdateBillingInfo(true));
    await wait(100);
    cancelCurrentRequestPlan();
    handlePurchase({
      billingInterval: billing?.recurring_interval,
      billing: billing,
      callback: navigateToPlans,
      payload: handleCreatePayload(),
    });
  };

  const confirmAndPay = () => {
    return (
      <div
        className={
          'd-flex flex-column justify-content-center align-items-center gap-2 bg-white p-4 mt-auto'
        }>
        <ul className={styles.prices}>
          {isCancelAtPeriod && (
            <li>
              <span>
                <span style={{ color: 'red', fontSize: '12px' }} className="m-0">
                  *Your Creator Plan is set for cancellation. This purchase will restore your plan.
                </span>
              </span>
            </li>
          )}
          <li>
            <span>
              Total
              {(proRata || proRata === 0 || subscriptionId) && (
                <small>
                  <span className="m-0 ms-1">(Amount will be charged on pro-rata basis)</span>
                </small>
              )}
            </span>
            {loadingProrate ? (
              <CircularProgress size={14} thickness={1} />
            ) : (
              `$${proRata || getTotalPrice()}`
            )}
          </li>
        </ul>
        <Button
          size="xlg"
          color="primary"
          className="w-100"
          style={{
            borderRadius: '10px',
          }}
          onClick={() => {
            trackClickEvent('Confirm & Pay', {
              cost: `$${proRata || getTotalPrice()}`,
              plan: 'Creator',
              is_yearly: billingCycle === 2,
              'Addon type': '',
            });

            handlePayment();
          }}
          loading={loading}
          disabled={loading || loadingProrate}>
          Confirm & Pay
        </Button>
        {/* {acceptServiceAgreement()} */}
      </div>
    );
  };

  const summaryCard = () => {
    return (
      <div className={styles.summaryContainerData}>
        <div className={styles.summaryContainer}>
          <h2 className={styles.summaryTitle}>Summary</h2>
          <h2 className={styles.subTitle}>Creator Plan</h2>
          {Object.keys(checkoutInfo)?.map((list) => {
            const price =
              +checkoutInfo?.[list]?.unit_amount ||
              +checkoutInfo?.[list]?.pricing_details?.unit_amount ||
              0;

            const isEditor = [checkoutInfo?.[list]?.name || list].includes(
              ADD_ONS_PRODUCT_BASE_NAME.Editor
            );

            let name = isEditor
              ? checkoutInfo?.[list]?.name || list
              : checkoutInfo?.[list]?.nickname || checkoutInfo?.[list]?.name || list;

            if (
              isEditor &&
              checkoutInfo?.[list]?.stripe_price_id &&
              (checkoutInfo?.[list]?.actualQty || checkoutInfo?.[list]?.qty)
            ) {
              name = `${name} - ${checkoutInfo?.[list]?.actualQty || checkoutInfo?.[list]?.qty} ${
                checkoutInfo?.[list]?.actualQty &&
                +checkoutInfo?.[list]?.actualQty !== +checkoutInfo?.[list]?.qty
                  ? `(${+checkoutInfo?.[list]?.qty})`
                  : ''
              }`;
            }
            if (!checkoutInfo[list] || !price) return;
            return (
              <div className={styles.item} key={list}>
                <span className={styles.itemLabel}>{name}</span>
                <span className={styles.itemPrice}>
                  $
                  {convertStripeCentsToAmount(
                    (+checkoutInfo?.[list]?.actualQty || +checkoutInfo?.[list]?.qty || 1) *
                      (+checkoutInfo?.[list]?.unit_amount ||
                        +checkoutInfo?.[list]?.pricing_details?.unit_amount ||
                        0)
                  )}
                  <span className="text-muted fw-normal">
                    {billingCycle === 1 ? '/month' : '/year'}
                  </span>
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (checkoutAPICallStatus === MAKE_CHECKOUT_API_CALL) {
      handlePayment();
      dispatch(subscriptionActions.setMakeCheckoutAPICall(null));
    }
  }, [checkoutAPICallStatus]);

  return (
    <div className={styles.checkoutUI}>
      <PlanBreadcrumb goBack={goBack} />
      <div className={styles.checkoutUI__container}>
        <div
          style={{
            border: '1px solid var(--dark-500)',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            // overflow: 'hidden',
          }}>
          <BillingFormV2
            planInfoProps={{
              enableAccordion: true,
              keepOpen: true,
              containerStyle: {
                border: 'none',
                borderBottom: '1px solid var(--dark-500)',
                borderRadius: 0,
              },
              cardTitleStyle: {
                color: '#2B2A28',
              },
            }}
          />
          {/* {!isUploadPlanCheckout && ( */}
          <PaymentMethods
            designV2={true}
            className="rounded-0"
            containerStyle={{
              borderLeft: 'none',
              borderRight: 'none',
              borderTop: 'none',
            }}
            planInfoProps={{
              isYearly: billingCycle === 2,
              enableAccordion: true,
              keepOpen: true,
              cardTitleStyle: {
                color: '#2B2A28',
              },
            }}
          />
          {/* )} */}
          {/* {_pricing_details?.length > 0 ? (
            <Billing
              title="Billing cycle"
              className="border-0"
              style={{ borderBottom: '1px solid var(--dark-500)' }}>
              {_pricing_details?.map(({ id, recurring_interval, unit_amount }) => (
                <BillingCycle
                  key={id}
                  title={`Billed ${getBillingCycleName({
                    cycle: recurring_interval,
                  })?.toLowerCase()}`}
                  price={convertStripeCentsToAmount(+unit_amount)}
                  cycle={recurring_interval}
                  selected={
                    billing?.recurring_interval === recurring_interval && billing?.id === id
                  }
                  onClick={() => onChangeBilling(recurring_interval, id)}
                  floatingText={
                    recurring_interval === RECURRING_INTERVAL.YEAR ? '2 Months Free' : ''
                  }
                />
              ))}
            </Billing>
          ) : null} */}
          {/* {!isUploadPlanCheckout && confirmAndPay()} */}
        </div>

        {/* plan information */}
        <div className={styles.checkoutInfo}>
          {_pricing_details?.length > 0 ? (
            <Billing
              title="Billing cycle"
              className="border-0"
              style={{ borderBottom: '1px solid var(--dark-500)' }}>
              {_pricing_details?.map(({ id, recurring_interval, unit_amount }) => (
                <BillingCycle
                  key={id}
                  title={`Billed ${getBillingCycleName({
                    cycle: recurring_interval,
                  })?.toLowerCase()}`}
                  price={convertStripeCentsToAmount(+unit_amount)}
                  cycle={recurring_interval}
                  selected={
                    billing?.recurring_interval === recurring_interval && billing?.id === id
                  }
                  onClick={() => onChangeBilling(recurring_interval, id)}
                  floatingText={
                    recurring_interval === RECURRING_INTERVAL.YEAR ? '2 Months Free' : ''
                  }
                />
              ))}
            </Billing>
          ) : null}
          {summaryCard()}
          {confirmAndPay()}
          {/* {isUploadPlanCheckout && (
            <>
              <PaymentMethods
                designV2={true}
                className="rounded-0"
                containerStyle={{
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  flex: 'none',
                }}
                planInfoProps={{
                  enableAccordion: true,
                  keepOpen: true,
                  cardTitleStyle: {
                    color: '#2B2A28',
                  },
                }}
              />
              {summaryCard()}
              {confirmAndPay()}
            </>
          )} */}
          {/* {!isUploadPlanCheckout && (
            <>
              <div className={styles.planPriceInfo}>
                {ImagineLogo({ width: 65 })}
                <h3 className={styles.name}>{name}</h3>
                <h2 className={styles.price}>${price}</h2>
                <p className={cx(styles.mutedText, 'd-flex align-items-center')}>
                  <AiFillUnlock />
                  &nbsp;&nbsp;Secure payment
                </p>
              </div>
              <div className={styles.features}>
                <h2>Plan includes</h2>
                <PlanDetails
                  features={billing?.feature_details}
                  style={{
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                  }}
                  designV2={true}
                />
              </div>
            </>
          )} */}
        </div>
      </div>
    </div>
  );
};

export const CheckoutUIAddons = ({
  checkoutInfo: addonsCheckoutInfo,
  goBack,
  handlePurchase,
  billingCycle,
  setBillingCycle,
  loading = false,
  isUploadPlanCheckout = true,
  isAddon = true,
  handleAddAddonCart,
  subscriptionDetails,
  setTotalCartAmount,
  isCurrentPlanIsCancelled,
}) => {
  const { purchaseAddOns } = useAddOnsInfo();
  const checkoutAPICallStatus = useSelector(selectIsMakeCheckoutAPICall);
  const dispatch = useDispatch();
  const [billing, setBilling] = useState();
  const [billingIntervals, setBillingIntervals] = useState(null);
  const [proRata, setProRata] = useState(null);
  const [loadingProrate, setLoadingProrate] = useState(false);
  const { userPlans, handleRestorePlan } = useSubscriptionPurchase();
  const { billing_name, billing_country, billing_email, billing_pincode } =
    useSelector(selectBillingInfo);
  const defaultPaymentMethod = useSelector(selectDefaultPaymentMethod);
  const [dataForSegmentLocal, setDataForSegmentLocal] = useState(null);
  const { trackClickEvent } = userPilotEvents();

  const { results: cartAddonsList } = useSelector(selectCartAddonsList);

  const subscriptionId =
    subscriptionDetails?.id ||
    addonsCheckoutInfo?.subscriptionId ||
    userPlans?.find(({ plan_details }) =>
      plan_details?.find(({ plan_id }) => +plan_id === +addonsCheckoutInfo?.id)
    )?.id;
  const isCancelAtPeriod = isCurrentPlanIsCancelled;

  const subscriptionList = useMemo(
    () =>
      [...getSubscriptionListForCombinePlanPurchase()]
        .map((a) => {
          if (cartAddonsList?.find((item) => item?.subscription_price === a?.product)) return a;

          return null;
        })
        .filter(Boolean),
    [cartAddonsList]
  );

  const noSubscriptionSelected = subscriptionList?.length === 0;

  // Combines the subscription list and addon checkout information,
  // filtering out any addons that are already included in the subscription list.
  const checkoutInfo = useMemo(
    () => [
      ...subscriptionList,
      ...addonsCheckoutInfo
        ?.filter((item) => {
          if (subscriptionList?.find((a) => a?.basemodeltype === item?.basemodeltype)) {
            return null;
          }
          return item;
        })
        .filter(Boolean),
    ],
    [addonsCheckoutInfo]
  );

  const isSubscriptionPlan = checkoutInfo?.some(({ is_subscription_plan }) => is_subscription_plan);
  const isOneTimePlan = checkoutInfo?.some(
    ({ is_one_time, is_subscription_plan }) => is_one_time && !is_subscription_plan
  );
  const isRecurringTimePlan = checkoutInfo?.some(
    ({ is_one_time, is_subscription_plan }) => !is_one_time && !is_subscription_plan
  );
  const bothAddonsAvailable = isOneTimePlan && isRecurringTimePlan;

  useEffect(() => {
    if (subscriptionId && isRecurringTimePlan) {
      getPlanProRataAmount();
    } else {
      proRata && setProRata(null);
    }
  }, [checkoutInfo, billing]);

  const handleCreatePayloadForProrateAPI = () => {
    let payload = {
      client_subscription_id: subscriptionId,
      upload_limit_prices: [],
      recurring_prices: [],
      one_time_items: [],
    };
    checkoutInfo?.length &&
      checkoutInfo?.map((list) => {
        if (!list) return;
        if (
          list?.basemodeltype === TEXTURE_BASE_TYPE ||
          list?.basemodeltype === THREE_3D_MODAL_BASE_TYPE
        ) {
          if (list?.stripe_price_id) {
            payload.upload_limit_prices.push({
              price_id: list?.stripe_price_id,
              quantity: 1,
              product_type: list?.productType,
            });
          }
        }
        if (list?.basemodeltype === ADD_ONS_PRODUCT_BASE_NAME.Editor && list?.stripe_price_id) {
          payload.recurring_prices.push({
            price_id: list?.stripe_price_id,
            quantity: list?.qty || list?.quantity || 1,
          });
        }
        // if (list?.is_add_on && list?.is_one_time) {
        //   const getPrice = list?.prices?.[0];
        //   if (getPrice) {
        //     payload.one_time_items.push({
        //       price_id: getPrice?.stripe_price_id,
        //       quantity: list?.qty || list?.quantity || 1,
        //     });
        //   }
        // }
        if (list?.is_add_on && !list?.is_one_time) {
          const getPrice = list?.prices?.find(
            ({ recurring_interval }) => +recurring_interval === +billingCycle
          );
          if (getPrice) {
            payload.recurring_prices.push({
              price_id: getPrice?.stripe_price_id,
              quantity: list?.qty || list?.quantity || 1,
            });
          }
        }
      });
    return payload;
  };

  const getPlanProRataAmount = async () => {
    setLoadingProrate(true);
    const payload = handleCreatePayloadForProrateAPI();
    if (payload) {
      const res = await GetProRataPriceV2(payload);
      setLoadingProrate(false);
      if (res?.data?.pro_rata_amount) {
        setProRata(res?.data?.pro_rata_amount?.toFixed(2) || null);
      } else {
        proRata && setProRata(null);
      }
    } else {
      proRata && setProRata(null);
    }
  };

  // useEffect(() => {
  //   console.log('DEBUG__CHECKOUT', {
  //     checkoutInfo,
  //     priceDetails: window.$priceDetails,
  //     subscriptionList,
  //     billing,
  //   });
  // }, [checkoutInfo, subscriptionList, billing]);

  useEffect(async () => {
    if (checkoutInfo?.length) {
      if (!billing_country || !billing_email || !billing_pincode || !billing_name) {
        return;
      }
      let filterCounts = userPlans?.[0]?.included_addons;
      if (!filterCounts?.length) {
        filterCounts = await apiGetAllProductUsedCount();
        filterCounts = filterCounts?.results;
      }
      const textureCount = filterCounts?.find((val) => val.basemodeltype === 'Texture');
      const modelCount = filterCounts?.find(
        (val) => val.basemodeltype === '3D Models Upload Limit'
      );
      const modelVariation = checkoutInfo?.find(
        (val) => val.basemodeltype === '3D Model variation'
      );
      const configurator = checkoutInfo?.find((val) => val.basemodeltype === 'Configurator');
      const video = checkoutInfo?.find((val) => val.basemodeltype === 'Video');
      const editor = checkoutInfo?.find((val) => val.basemodeltype === 'Editor');
      const model = checkoutInfo?.find((val) => val.basemodeltype === '3D Model');
      const arView = checkoutInfo?.find((val) => val.basemodeltype === '360 Viewer + AR');
      const seamless = checkoutInfo?.find(
        (val) => val.basemodeltype === FABRIC_SEAMLESS_PRODUCTION_CREATION_BASE_TYPE
      );

      const dataForSegment = {
        Full_Name: billing_name,
        Email_Address: billing_email,
        Country: billing_country,
        Zipcode: billing_pincode,
        Payment_Method: defaultPaymentMethod?.payment_type || '',
        Editor: editor?.quantity || 0,
        Video: video?.quantity || 0,
        Configurator: configurator?.quantity || 0,
        Current_Plan_Name: userPlans?.length ? userPlans?.[0]?.planDetails?.[0]?.plan_name : 'Free',
        Texture_Limit: textureCount?.total_count || 0,
        Model_Limit: modelCount?.total_count || 0,
        Subscription_Type: getBillingCycleName({ cycle: billing?.interval }),
      };
      dataForSegment['3D_Model'] = model?.quantity || 0;
      dataForSegment['3D_Model_Variation'] = modelVariation?.quantity || 0;
      dataForSegment['3DViewer_AR'] = arView?.quantity || 0;
      dataForSegment['Fabric_Seamless_Creation'] = seamless?.quantity || 0;
      setDataForSegmentLocal(dataForSegment);
    }
  }, [checkoutInfo]);

  useEffect(() => {
    return () => {
      setOverflowAutoInBody();
    };
  }, []);

  const totalOneTimeAddons = (list) => {
    let amount = 0;
    list?.forEach((_item) => {
      let itemAmount = 0;
      _item?.prices?.forEach((__item) => {
        if (_item?.is_one_time) {
          itemAmount += +__item?.unit_amount;
        }
      });
      amount += itemAmount * _item?.quantity;
    });
    return amount;
  };

  const resetStates = () => {
    setBilling(null);
    goBack?.();
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('ignoreIsSendPrice') && params.get('referrer')) {
      window.referrer = params.get('referrer');
    }
    return async () => {
      resetStates();
      if (
        params.get('checkout') === 'true' ||
        params.get('is_one_time') === 'true' ||
        params.get('is_one_time') === 'false'
      ) {
        params.delete('checkout');
        params.delete('is_one_time');
        params.delete('referrer');
        history.replace({
          search: params.toString(),
        });

        await removeAllItemsFromCart();
        dispatch(GetAllAddonsProductList({ is_new_self_serve_plan: true }));
        dispatch(GetAllCartProductList());
      }
    };
  }, []);

  const onChangeBilling = (selectedInterval) => {
    if (!selectedInterval) return;
    setBilling({ ...selectedInterval });
    setBillingCycle(selectedInterval?.interval);
  };

  const cancelCurrentRequestPlan = async () => {
    if (isCancelAtPeriod && isRecurringTimePlan) {
      handleRestorePlan?.();
    }
    return null;
  };

  const confirmAndPay = () => {
    const addDataToSegment = async () => {
      if (dataForSegmentLocal?.Current_Plan_Name) {
        callTrackEvent('Confirm & Pay', dataForSegmentLocal, true);
      }
    };

    const clickedPay = async ({ billingInterval, billing, callback }) => {
      addDataToSegment();
      dispatch(subscriptionActions.setUpdateBillingInfo(true));
      await wait(100);
      cancelCurrentRequestPlan();
      handlePurchase({
        billingInterval,
        billing,
        callback,
        totalAmt: loadingProrate
          ? 0
          : proRata ||
            convertStripeCentsToAmount(
              getTotalAmounts({ interval: billing?.interval, count: billing?.count })
            ),
      });
    };

    useEffect(() => {
      if (checkoutAPICallStatus === MAKE_CHECKOUT_API_CALL) {
        clickedPay({
          billingInterval: billing?.recurring_interval,
          billing: billing,
          callback: navigateToPlans,
        });
        dispatch(subscriptionActions.setMakeCheckoutAPICall(null));
      }
    }, [checkoutAPICallStatus]);

    return (
      <div
        className={
          'd-flex flex-column justify-content-center align-items-center gap-2 bg-white p-4 mt-auto'
        }>
        <ul className={styles.prices}>
          {/* {bothAddonsAvailable && (
            <>
              <li>
                <span>Recurring add-on total</span>
                <span>
                  $
                  {(proRata
                    ? +proRata
                    : convertStripeCentsToAmount(
                        getTotalAmounts(billing?.interval, billing?.count)
                      ) - +convertStripeCentsToAmount(totalOneTimeAddons(checkoutInfo))
                  )?.toFixed(2)}
                </span>
              </li>
              <li>
                <span>One-time add-on total</span>
                <span>
                  ${convertStripeCentsToAmount(totalOneTimeAddons(checkoutInfo))?.toFixed(2)}
                </span>
              </li>
            </>
          )} */}

          {isCancelAtPeriod && isRecurringTimePlan && (
            <li>
              <span>
                <span style={{ color: 'red', fontSize: '12px' }} className="m-0">
                  *Your Creator Plan is set for cancellation. This purchase will restore your plan.
                </span>
              </span>
            </li>
          )}

          <li>
            <span>
              Total{' '}
              {isRecurringTimePlan && proRata && (
                <small>
                  <span className="m-0">(Amount will be charged on pro-rata basis)</span>
                </small>
              )}
            </span>
            <span>
              {loadingProrate ? (
                <CircularProgress size={14} thickness={1} />
              ) : (
                `$${
                  proRata
                    ? bothAddonsAvailable
                      ? (
                          +proRata + +convertStripeCentsToAmount(totalOneTimeAddons(checkoutInfo))
                        ).toFixed(2)
                      : proRata
                    : convertStripeCentsToAmount(
                        getTotalAmounts({ interval: billing?.interval, count: billing?.count })
                      )
                }`
              )}
            </span>
          </li>
        </ul>
        <Button
          size="xlg"
          color="primary"
          className="w-100"
          style={{
            borderRadius: '10px',
          }}
          onClick={() => {
            trackClickEvent('Confirm & Pay', {
              cost: `$${
                proRata
                  ? bothAddonsAvailable
                    ? (
                        +proRata + +convertStripeCentsToAmount(totalOneTimeAddons(checkoutInfo))
                      ).toFixed(2)
                    : proRata
                  : convertStripeCentsToAmount(
                      getTotalAmounts({ interval: billing?.interval, count: billing?.count })
                    )
              }`,
              plan: 'Creator',
              is_yearly: billingCycle === 2,
              'Addon type': addonsCheckoutInfo?.reduce(
                (a, c) => (!c?.is_one_time ? a : `${a}${c?.basemodeltype}, `),
                ''
              ),
            });
            clickedPay({
              billingInterval: billing?.recurring_interval,
              billing: billing,
              callback: navigateToPlans,
            });
          }}
          loading={loading}
          disabled={loading || loadingProrate}>
          Confirm & Pay
        </Button>
        {/* {acceptServiceAgreement()} */}
      </div>
    );
  };

  useEffect(() => {
    handleShowBillingCycleList();
    if (!checkoutInfo?.length) {
      resetStates();
    }
  }, [checkoutInfo]);

  const handleShowBillingCycleList = () => {
    let _isQuarterly = false;
    let _isSemiAnnually = false;
    let _isMonthly = false;
    let _isYearly = false;
    checkoutInfo?.forEach((_item) => {
      if (!_isSemiAnnually) {
        _isSemiAnnually = _item?.prices?.find(
          (__item) =>
            __item?.recurring_interval === 1 && __item?.recurring_interval_count?.toString() === '6'
        )
          ? { interval: 1, count: 6, totalAmount: 0 }
          : false;
      }
      if (!_isQuarterly) {
        _isQuarterly = _item?.prices?.find(
          (__item) =>
            __item?.recurring_interval === 1 && __item?.recurring_interval_count?.toString() === '3'
        )
          ? { interval: 1, count: 3, totalAmount: 0 }
          : false;
      }
      if (!_isMonthly) {
        _isMonthly = _item?.prices?.find(
          (__item) =>
            __item?.recurring_interval === 1 && __item?.recurring_interval_count?.toString() === '1'
        )
          ? { interval: 1, count: 1, totalAmount: 0 }
          : false;
      }
      if (!_isYearly) {
        _isYearly = _item?.prices?.find(
          (__item) =>
            __item?.recurring_interval === 2 && __item?.recurring_interval_count?.toString() === '1'
        )
          ? { interval: 2, count: 1, totalAmount: 0 }
          : false;
      }
    });
    if (_isMonthly) {
      _isMonthly.totalAmount = getTotalAmounts({ internal: 1, count: 1, excludeOneTime: true });
    }
    if (_isQuarterly) {
      _isQuarterly.totalAmount = getTotalAmounts({ interval: 1, count: 3, excludeOneTime: true });
    }
    if (_isSemiAnnually) {
      _isSemiAnnually.totalAmount = getTotalAmounts({
        interval: 1,
        count: 6,
        excludeOneTime: true,
      });
    }
    if (_isYearly) {
      _isYearly.totalAmount = getTotalAmounts({ interval: 2, count: 1, excludeOneTime: true });
    }
    setBillingIntervals({
      Monthly: _isMonthly,
      Quarterly: _isQuarterly,
      ['Semi Annually']: _isSemiAnnually,
      Yearly: _isYearly,
    });
    if (!billing) {
      let cycle = _isMonthly || _isQuarterly || _isSemiAnnually || _isYearly;
      if (billingCycle === RECURRING_INTERVAL.YEAR) {
        // if we have yearly plan then set it as selected by moving to the first position
        cycle = _isYearly || _isMonthly || _isQuarterly || _isSemiAnnually;
      }
      setBilling(cycle);
      cycle?.interval && setBillingCycle(cycle?.interval);
    }
  };

  const getTotalAmounts = ({ interval = 1, counts = 1, excludeOneTime = false }) => {
    let amount = 0;

    checkoutInfo?.forEach((_item) => {
      let itemAmount = 0;
      _item?.prices?.forEach((__item) => {
        if (
          _item?.is_one_time ||
          (__item?.recurring_interval === interval && +__item?.recurring_interval_count === +counts)
        ) {
          itemAmount += _item?.is_one_time && excludeOneTime ? 0 : +__item?.unit_amount;
        }
      });

      amount += itemAmount * _item?.quantity;

      if ('finalAmount' in _item) {
        // the finalAmount key i'm adding for the subscription plan data only
        amount += +_item?.finalAmount;
      }
    });

    setTotalCartAmount(amount);

    return amount;
  };

  const getItemPrice = (item = null) => {
    if (!item) return 0;

    if (!('prices' in item)) {
      return (
        convertStripeCentsToAmount(+item?.unit_amount, true) *
        (+(item?.actualQty || item?.qty) || 1)
      );
    }

    const activePrice = item?.prices?.find(
      (a) =>
        item?.is_one_time ||
        (a.recurring_interval === billing?.interval &&
          +a.recurring_interval_count === +billing?.count)
    );
    return convertStripeCentsToAmount(+activePrice?.unit_amount, true) * +item?.quantity || 0;
  };

  const handleListAddonsItems = (item) => {
    const priceValue = getItemPrice(item);
    return priceValue ? (
      <>
        <div className={styles.item} key={item?.id}>
          <span className={styles.itemLabel}>{item?.name}</span>
          <div className={styles.priceAndCountContainer}>
            {item?.basemodeltype === MATTRESS_BUILDER_BASE_TYPE ? (
              <Button
                size={'xlg'}
                style={{
                  backgroundColor: '#F8F8F8',
                  color: '#141414',
                  border: '0px',
                  opacity: '1',
                  display: 'flex',
                  height:"35px",
                  borderRadius:"15px"
                }}
                onClick={() => handleAddAddonCart(item, false)}>
                Remove
              </Button>
            ) : (
              <div className={styles.qtyBtn}>
                <MinusIcon
                  fill="#2B2A28"
                  className="cursor-pointer"
                  onClick={() => handleAddAddonCart(item, false)}
                />
                <span className={styles.addonsQty}>{item?.quantity} </span>
                <PlusIcon
                  fill="#2B2A28"
                  className="cursor-pointer"
                  onClick={() => handleAddAddonCart(item, true)}
                />
              </div>
            )}

            <span className={styles.itemPrice}>
              {' '}
              ${priceValue}{' '}
              <span className="text-muted fw-normal">
                {item?.is_one_time ? '/one-time' : billingCycle === 1 ? '/month' : '/year'}
              </span>
            </span>
          </div>
        </div>
      </>
    ) : null;
  };

  const summaryCard = () => {
    return (
      <div className={styles.summaryContainerData}>
        <div className={styles.summaryContainer}>
          <h2 className={styles.summaryTitle}>Summary</h2>

          {isSubscriptionPlan ? (
            <>
              <h2 className={styles.subTitle}>Creator Plan</h2>
              {checkoutInfo
                .filter((a) => a.is_subscription_plan)
                .map((item) => {
                  let name = item?.name;
                  if (
                    item?.basemodeltype === ADD_ONS_PRODUCT_BASE_NAME.Editor &&
                    item?.stripe_price_id &&
                    item?.qty
                  ) {
                    name = `${name} - ${item?.actualQty || item?.qty} ${
                      item?.actualQty && +item?.actualQty !== +item?.qty ? `(${+item?.qty})` : ''
                    }`;
                  }
                  return (
                    <div className={styles.item} key={item?.id}>
                      <span className={styles.itemLabel}>{name}</span>
                      <span className={styles.itemPrice}>
                        ${getItemPrice(item)}
                        <span className="text-muted fw-normal">
                          {billingCycle === 1 ? '/month' : '/year'}
                        </span>
                      </span>
                    </div>
                  );
                })}

              {isRecurringTimePlan || isOneTimePlan ? (
                <h2 className={cx(styles.subTitle, 'mt-3')}>ADD-ONS</h2>
              ) : null}
            </>
          ) : null}

          {!bothAddonsAvailable &&
            checkoutInfo
              ?.filter((a) => !a.is_subscription_plan)
              ?.map((item) => {
                return handleListAddonsItems(item);
              })}
          {bothAddonsAvailable && isRecurringTimePlan && (
            <h2 className={styles.subTitle}>Recurring</h2>
          )}
          {bothAddonsAvailable &&
            checkoutInfo
              ?.filter(
                ({ is_one_time, is_subscription_plan }) => !is_one_time && !is_subscription_plan
              )
              ?.map((item) => {
                return handleListAddonsItems(item);
              })}
          {bothAddonsAvailable && isOneTimePlan && <h2 className={styles.subTitle}>One time</h2>}
          {bothAddonsAvailable &&
            checkoutInfo
              ?.filter(
                ({ is_one_time, is_subscription_plan }) => is_one_time && !is_subscription_plan
              )
              ?.map((item) => {
                return handleListAddonsItems(item);
              })}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.checkoutUI}>
      <PlanBreadcrumb goBack={goBack} />
      <div className={styles.checkoutUI__container}>
        <div
          style={{
            border: '1px solid var(--dark-500)',
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            // overflow: 'hidden',
          }}>
          <BillingFormV2
            planInfoProps={{
              enableAccordion: true,
              keepOpen: true,
              cardTitleStyle: {
                color: '#2B2A28',
              },
            }}
          />
          {/* {(!isUploadPlanCheckout || !isAddon) && (  */}
          <PaymentMethods
            designV2={true}
            className="rounded-0"
            containerStyle={{
              borderLeft: 'none',
              borderRight: 'none',
              borderTop: 'none',
            }}
            dataForSegment={dataForSegmentLocal}
            planInfoProps={{
              isYearly: billingCycle === 2,
              enableAccordion: true,
              keepOpen: true,
              cardTitleStyle: {
                color: '#2B2A28',
              },
            }}
          />
          {/* )} */}
          {/* {billingIntervals &&
          Object.keys(billingIntervals)?.filter((keyData) => billingIntervals[keyData])?.length >
            0 ? (
            <Billing
              title="Billing cycle"
              className="border-0"
              style={{ borderBottom: '1px solid var(--dark-500)' }}>
              {Object.keys(billingIntervals)?.map((key) => {
                return !billingIntervals[key] ? null : (
                  <BillingCycle
                    key={key}
                    title={key}
                    price={convertStripeCentsToAmount(+billingIntervals[key]?.totalAmount) || 0}
                    cycle={billingIntervals[key].interval}
                    selected={
                      billing?.interval === billingIntervals[key]?.interval &&
                      billing?.count === billingIntervals[key]?.count
                    }
                    onClick={() => onChangeBilling(billingIntervals[key])}
                  />
                );
              })}
            </Billing>
          ) : null} */}
          {/* {(!isUploadPlanCheckout || !isAddon) && confirmAndPay()} */}
        </div>

        {/* plan information */}
        <div className={styles.checkoutInfo}>
          {billingIntervals &&
          noSubscriptionSelected &&
          Object.keys(billingIntervals)?.filter((keyData) => billingIntervals[keyData])?.length >
            0 ? (
            <Billing
              title="Billing cycle"
              className="border-0"
              style={{ borderBottom: '1px solid var(--dark-500)' }}>
              {Object.keys(billingIntervals)?.map((key) => {
                return !billingIntervals[key] ? null : (
                  <BillingCycle
                    key={key}
                    title={key}
                    price={convertStripeCentsToAmount(+billingIntervals[key]?.totalAmount) || 0}
                    cycle={billingIntervals[key].interval}
                    selected={
                      billing?.interval === billingIntervals[key]?.interval &&
                      billing?.count === billingIntervals[key]?.count
                    }
                    onClick={() => onChangeBilling(billingIntervals[key])}
                  />
                );
              })}
            </Billing>
          ) : null}

          {summaryCard()}
          {confirmAndPay()}

          {/* {(isUploadPlanCheckout || isAddon) && (
            <>
              <PaymentMethods
                designV2={true}
                className="rounded-0"
                containerStyle={{
                  borderLeft: 'none',
                  borderRight: 'none',
                  borderTop: 'none',
                  flex: 'none',
                }}
                dataForSegment={dataForSegmentLocal}
                planInfoProps={{
                  enableAccordion: true,
                  keepOpen: true,
                  cardTitleStyle: {
                    color: '#2B2A28',
                  },
                }}
              />
              {summaryCard()}
              {confirmAndPay()}
            </>
          )} */}
          {/* {(!isUploadPlanCheckout || !isAddon) && (
            <>
              <div className={styles.planPriceInfo}>
                {ImagineLogo({ width: 65 })}
                <h3 className={styles.name}>{name}</h3>
                <h2 className={styles.price}>${price}</h2>
                <p className={cx(styles.mutedText, 'd-flex align-items-center')}>
                  <AiFillUnlock />
                  &nbsp;&nbsp;Secure payment
                </p>
              </div>
              <div className={styles.features}>
                <h2>Plan includes</h2>
                <PlanDetails
                  features={billing?.feature_details}
                  style={{
                    borderTop: 'none',
                    borderLeft: 'none',
                    borderRight: 'none',
                  }}
                  designV2={true}
                />
              </div>
            </>
          )} */}
        </div>
      </div>
    </div>
  );
};
